import {
  Bleed,
  CustomPagination,
  DataObjectGroupType,
  DataRowType,
  DetailLayout,
  EMPTY_PAGE,
  formatDate,
  GetApiErrorMessage,
  InputDataInfoType,
  InputDataKey,
  InputDataType,
  isSafeFiniteInteger,
  keepStaleInMinutes,
  Padding,
  PopconfirmDelete,
  removeNullish,
  Spinner,
  TotalEntries,
  useDataObjectGroupsWithDescriptionsQuery,
  useDescriptionInputDataInfoQuery,
  useDescriptionInputDataQuery,
  useDirectory,
  useInputDataDeleteMutation,
  useLoadViewState,
  useQueryParams,
  useRefetchDescriptionInputDataQueries,
  useSaveDirectoryState,
  useAlertContext,
  ISODate,
  BookmarkCreate,
  saveViewState,
  InfoIconUrl,
  createApiErrorTranslator,
} from '@atlas-frontend/sdk';
import type { SortingState } from '@tanstack/react-table';
import type { PaginationProps } from 'antd';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { SectionBreadcrumbs } from '../../../common/SectionBreadcrumbs';
import { useNumericParam } from '../../../common/useEntityIdParam';
import { NotFound } from '../../NotFound';
import { SampleHeader } from '../../sample/SampleHeader';
import { SampleDataObjectType } from '../../sample/SampleSearch';
import {
  SampleContextProvider,
  useSampleContext,
} from '../../sample/state/SampleContext';
import { INPUT_DATA_ROUTES } from '../InputData';
import { Divider, Flex } from 'antd';
import { InputDataSampleTable } from '../table/InputDataSampleTable';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './InputDataDetail.translations.json';
import airflowTranslations from '../details/useInputDataActions.translations.json';
import { keepPreviousData } from '@tanstack/react-query';
import { InputDataActions } from './InputDataActions';
import { Table } from '@tanstack/react-table';
import { useInputDataParameters } from '../useInputDataParameters';

const { addTranslations, t, useTranslation, exists } =
  i18nNsFactory('input-data-detail');
addTranslations(translations);
addTranslations(airflowTranslations);
const translateMutationError: GetApiErrorMessage = createApiErrorTranslator({
  t,
  exists,
});

const directoryStateStorageKey = 'inputDataTable';

export function InputDataDetail() {
  const { groupOrDescriptionId, date, sampleType, selectedGroupMemberId } =
    useSampleContext();
  const { inputDataVersionId } = useInputDataParameters();
  const descriptionId =
    sampleType === 'description' ? groupOrDescriptionId : selectedGroupMemberId;

  const { data: info, isLoading } = useDescriptionInputDataInfoQuery(
    {
      descriptionId: descriptionId ?? 0,
      valueDate: date as ISODate,
      dataObjectVersionId: inputDataVersionId,
    },
    { enabled: !!descriptionId && !!date, throwOnError: true },
  );

  const { state: viewState, isQueryParamCleared } = useLoadViewState(
    directoryStateStorageKey,
  );

  if (isLoading) {
    return <Spinner overlay />;
  }

  if (!isQueryParamCleared) {
    return null;
  }

  if (!descriptionId || !date) {
    return <NotFound />;
  }

  if (!info) {
    return null;
  }

  return (
    <InputDataDetailInner
      info={info}
      initialFilters={viewState.filters}
      initialSort={viewState.sort}
      initialPagination={viewState.pagination}
      descriptionId={descriptionId}
    />
  );
}

interface InputDataDetailInnerProps {
  info: InputDataInfoType;
  initialFilters?: Partial<Record<string, any>>;
  initialSort?: SortingState;
  initialPagination?: PaginationProps;
  descriptionId: number;
}

function InputDataDetailInner({
  info,
  initialFilters,
  initialSort,
  initialPagination,
  descriptionId,
}: InputDataDetailInnerProps) {
  const { t } = useTranslation();
  const {
    groupOrDescriptionId,
    date,
    sampleType,
    groupOrDescription,
    selectedGroupMemberId,
  } = useSampleContext();
  const queryFilterKeys = useMemo(
    () => info.columns.map((c) => c.code),
    [info.columns],
  );
  const { paginationProps, queryParameters, setTotal, tableProps } =
    useDirectory({
      queryFilterKeys,
      initialFilters,
      initialSort,
      initialPagination,
    });

  useSaveDirectoryState(directoryStateStorageKey, {
    paginationProps,
    queryParameters,
    tableProps,
  });

  useEffect(() => {
    saveViewState('inputDataView', {
      id: groupOrDescriptionId,
      date,
      type: sampleType,
    });
  }, [groupOrDescriptionId, date, sampleType]);

  const refetchDescriptionInputDataQueries =
    useRefetchDescriptionInputDataQueries();
  const { data, isLoading: isLoadingGroups } =
    useDataObjectGroupsWithDescriptionsQuery(
      { groupType: DataObjectGroupType.I },
      {
        placeholderData: keepPreviousData,
        ...keepStaleInMinutes(1),
      },
    );
  const { mutateAsync, isPending: isDeletingInputData } =
    useInputDataDeleteMutation();
  const { resetAlert, setSuccess, setError } = useAlertContext();
  const tableRef = useRef<Table<DataRowType> | null>(null);
  const { inputDataVersionId, outputDataVersionId } = useInputDataParameters();

  const title = useMemo(
    () =>
      `${groupOrDescription?.code} - ${groupOrDescription?.name} - ${formatDate(
        date,
      )}`,
    [date, groupOrDescription],
  );

  const isSnapshot = !!inputDataVersionId;

  const { page, size, sort, ...allFilterParams } = queryParameters;
  const filterParams = useMemo(
    () => removeNullish(allFilterParams),
    [allFilterParams],
  );
  const {
    data: { content, totalElements, subjectNameMappings } = {
      ...EMPTY_PAGE,
      subjectNameMappings: {},
    },
    isFetching: isLoadingInputData,
    error,
  } = useDescriptionInputDataQuery(
    {
      valueDate: date,
      descriptionId,
      dataObjectVersionId: inputDataVersionId,
      page,
      size,
      sort,
      filterParams,
    },
    {
      enabled: !!descriptionId && !!date,
      placeholderData: keepPreviousData,
    },
  );
  const inputData: InputDataType = useMemo(
    () => ({
      columns: info.columns,
      labels: info.labels,
      rows: content,
    }),
    [info, content],
  );

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  const isLoading = isLoadingGroups || isLoadingInputData;

  const deleteDescriptionData = useCallback(() => {
    const data: InputDataKey = {
      id: descriptionId,
      sampleType: 'description',
      valueDate: date,
    };
    resetAlert();
    mutateAsync({
      data,
    })
      .then(async () => {
        await refetchDescriptionInputDataQueries();
        setSuccess(t('alert.success.deleted'));
      })
      .catch((error) => {
        setError(
          translateMutationError(error.response?.data?.errors?.[0]?.code),
        );
      });
  }, [
    descriptionId,
    date,
    resetAlert,
    mutateAsync,
    refetchDescriptionInputDataQueries,
    setSuccess,
    t,
    setError,
  ]);

  const deleteGroupData = useCallback(() => {
    resetAlert();
    if (sampleType === 'group') {
      const data: InputDataKey = {
        id: groupOrDescriptionId,
        sampleType: 'group',
        valueDate: date,
      };
      mutateAsync({
        data,
      })
        .then(async () => {
          await refetchDescriptionInputDataQueries();
          setSuccess(t('alert.success.deleted'));
        })
        .catch((error) => {
          setError(translateMutationError(error.response?.data?.errors?.[0]));
        });
    }
  }, [
    date,
    groupOrDescriptionId,
    mutateAsync,
    refetchDescriptionInputDataQueries,
    resetAlert,
    sampleType,
    setError,
    setSuccess,
    t,
  ]);

  const dropdownItems = useMemo(() => {
    if (isLoading || isSnapshot) {
      return [];
    }

    const items = [
      {
        key: 'deleteDescriptionData',
        label: (
          <PopconfirmDelete
            stopClickPropagation
            onDelete={deleteDescriptionData}
            genitiveModelName={t('delete.genitive.description')}
            disabled={isDeletingInputData}
          >
            {t('button.deleteDescriptionData')}
          </PopconfirmDelete>
        ),
      },
    ];

    if (sampleType === 'group') {
      items.push({
        key: 'deleteGroupData',
        label: (
          <PopconfirmDelete
            stopClickPropagation
            onDelete={deleteGroupData}
            genitiveModelName={t('delete.genitive.group')}
            disabled={isDeletingInputData}
          >
            {t('button.deleteGroupData')}
          </PopconfirmDelete>
        ),
      });
    }
    return items;
  }, [
    isLoading,
    isSnapshot,
    deleteDescriptionData,
    t,
    isDeletingInputData,
    sampleType,
    deleteGroupData,
  ]);
  useEffect(() => {
    // Reset delete errors if date or description/group is changed
    resetAlert();
  }, [groupOrDescriptionId, date, resetAlert]);

  const inputDataActions = useMemo(() => {
    if (isLoading || !!error) return undefined;
    return (
      <InputDataActions
        id={descriptionId}
        valueDate={date}
        dataObjectVersionId={inputDataVersionId}
        tableRef={tableRef}
        isDisabled={isDeletingInputData}
      />
    );
  }, [
    isLoading,
    error,
    descriptionId,
    date,
    inputDataVersionId,
    isDeletingInputData,
  ]);

  return (
    <DetailLayout
      infoIconUrl={InfoIconUrl.INPUT_DATA_DETAIL}
      breadCrumb={
        <SectionBreadcrumbs data={title} routes={INPUT_DATA_ROUTES} />
      }
      bookmarkCreate={
        <BookmarkCreate
          excludeUrlParams={!inputDataVersionId ? ['date'] : []}
          includeUrlParams={
            !inputDataVersionId
              ? [{ key: 'periodSkipLastValueDate', value: 'true' }]
              : []
          }
        />
      }
      footerContent={
        <>
          <TotalEntries count={paginationProps.total ?? 0} />
          <FooterPagination {...paginationProps} />
        </>
      }
      headerConfig={{
        actions: inputDataActions,
        dropdownMenu:
          dropdownItems.length > 0 ? { items: dropdownItems } : undefined,
      }}
    >
      <HeaderWrapper>
        <SampleHeader
          data={data}
          date={date}
          groupMemberId={selectedGroupMemberId}
          id={groupOrDescriptionId}
          isLoading={isLoading}
          sampleType={sampleType}
          view="input-data"
          snapshotData={info}
        />
        <Bleed direction="horizontal">
          <Divider />
        </Bleed>
      </HeaderWrapper>
      <ContentWrapper bottom left right>
        <Flex vertical gap="middle" align="center">
          <InputDataSampleTable
            date={date}
            id={descriptionId}
            groupId={sampleType === 'group' ? groupOrDescriptionId : undefined}
            outputDataVersionId={outputDataVersionId}
            data={inputData}
            isFetching={isLoadingInputData}
            error={error}
            ref={tableRef}
            tableProps={tableProps}
            subjectNameMappings={subjectNameMappings}
          />
        </Flex>
      </ContentWrapper>
    </DetailLayout>
  );
}

export const InputDataDetailWithContext = () => {
  const id = useNumericParam('id');
  const [{ date, groupMemberId, type, periodSkipLastValueDate = false }] =
    useQueryParams();
  const dataObjectType = type as SampleDataObjectType;

  if (!id || !dataObjectType) {
    return <NotFound />;
  }

  return (
    <SampleContextProvider
      dataObjectType={dataObjectType}
      date={date as ISODate}
      periodSkipLastValueDate={periodSkipLastValueDate as boolean}
      id={id}
      groupMemberId={
        isSafeFiniteInteger(groupMemberId) ? Number(groupMemberId) : undefined
      }
      sampleView={false}
    >
      <InputDataDetail />
    </SampleContextProvider>
  );
};

const HeaderWrapper = styled(Padding)`
  position: sticky;
  left: 50%;
  transform: translateX(-50%);
  margin-inline: -999999px;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled(Padding)`
  width: 100%;
  min-width: fit-content;
  min-height: fit-content;
`;

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;
